"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SlideMedia, hasSlideMedia, type SlideMediaFields } from "../slide-media";

interface Slide extends SlideMediaFields {
  title: string;
  subtitle?: string;
  description?: string;
  linkUrl?: string;
  buttonText?: string;
}

interface BotanicalEditorialHeroProps {
  slides: Slide[];
}

const ROTATE_MS = 5200;

export function BotanicalEditorialHero({ slides }: BotanicalEditorialHeroProps) {
  const visibleSlides = useMemo(() => slides.filter(hasSlideMedia), [slides]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || visibleSlides.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % visibleSlides.length);
    }, ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [paused, visibleSlides.length]);

  if (visibleSlides.length === 0) return null;

  const slide = visibleSlides[active] ?? visibleSlides[0];
  const goToPrevious = () => setActive((current) => (current - 1 + visibleSlides.length) % visibleSlides.length);
  const goToNext = () => setActive((current) => (current + 1) % visibleSlides.length);

  return (
    <section
      data-hero="botanical-background-slider"
      className="relative left-1/2 min-h-screen w-screen -translate-x-1/2 overflow-hidden bg-[var(--sf-text)]"
      aria-roledescription="carousel"
      aria-label="Botanical vitrin slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {visibleSlides.map((item, index) => {
        const isActive = index === active;
        return (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ease-out ${
              isActive ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={!isActive}
          >
            <SlideMedia
              slide={item}
              priority={index === 0}
              sizes="100vw"
              className="object-cover"
            />
          </div>
        );
      })}

      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(17,24,12,0.74),rgba(17,24,12,0.42)_42%,rgba(17,24,12,0.1)_72%),linear-gradient(0deg,rgba(17,24,12,0.5),rgba(17,24,12,0.08)_40%,rgba(17,24,12,0.2))]" />

      {visibleSlides.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Önceki slide"
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/35 bg-white/10 text-white backdrop-blur-md transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70 sm:left-8 sm:h-14 sm:w-14"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label="Sonraki slide"
            onClick={goToNext}
            className="absolute right-4 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/35 bg-white/10 text-white backdrop-blur-md transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70 sm:right-8 sm:h-14 sm:w-14"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}

      <div className="relative z-10 flex min-h-screen items-center px-6 pb-24 pt-36 sm:px-10 lg:px-24 lg:pt-44">
        <div className="max-w-3xl">
          {slide.subtitle && (
            <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.34em] text-[var(--sf-hero-subtitle)]">
              {slide.subtitle}
            </p>
          )}
          <h1
            className="max-w-3xl text-balance text-5xl leading-[0.94] text-[var(--sf-hero-title)] sm:text-7xl lg:text-8xl"
            style={{ fontFamily: "var(--sf-font-heading)" }}
          >
            {slide.title}
          </h1>
          {slide.description && (
            <p
              className="mt-7 max-w-xl text-base leading-8 sm:text-lg"
              style={{ color: "color-mix(in srgb, var(--sf-hero-title) 82%, transparent)" }}
            >
              {slide.description}
            </p>
          )}
          {slide.linkUrl && slide.buttonText && (
            <Link href={slide.linkUrl} className="mt-9 inline-flex">
              <Button
                size="lg"
                className="rounded-full bg-[var(--sf-hero-btn-bg)] px-8 text-[13px] font-semibold uppercase tracking-[0.2em] text-[var(--sf-hero-btn-text)] shadow-[0_18px_45px_rgba(0,0,0,0.18)] hover:bg-[var(--sf-hero-btn-bg)] hover:opacity-90"
              >
                {slide.buttonText}
              </Button>
            </Link>
          )}
        </div>
      </div>

      {visibleSlides.length > 1 && (
        <div className="absolute bottom-7 left-6 z-20 flex items-center gap-3 sm:left-10 lg:left-24">
          {visibleSlides.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActive(index)}
              className="h-1.5 rounded-full bg-white/45 transition-all hover:bg-white"
              style={{ width: active === index ? 42 : 14, backgroundColor: active === index ? "var(--sf-hero-btn-bg)" : undefined }}
              aria-label={`${index + 1}. slide'a git`}
              aria-current={active === index}
            />
          ))}
        </div>
      )}
    </section>
  );
}
